import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { PageHead, Skel } from '../components/ui'
import { thaiDate } from '../lib/format'

interface VaultKey {
  id: string
  purpose: string
  version: number
  active: boolean
  created_at: string
  retired_at?: string
  age_days: number
  rotate_recommended: boolean
}

interface VaultOverview {
  max_age_days: number
  keys: VaultKey[]
}

const PURPOSES = [
  {
    id: 'mfa',
    label: 'คีย์เข้ารหัส MFA',
    hint: 'ใช้เข้ารหัส secret ของแอป Authenticator ก่อนบันทึก',
  },
  {
    id: 'field_crypto',
    label: 'คีย์เข้ารหัสฟิลด์ข้อมูล',
    hint: 'ใช้ห่อหุ้มเลขบัญชีธนาคารและเลขประจำตัวผู้เสียภาษี',
  },
  {
    id: 'api_key_pepper',
    label: 'คีย์ผสม API key',
    hint: 'ใช้เสริมการแฮชคีย์ API ก่อนเก็บลงฐานข้อมูล',
  },
  {
    id: 'report_signing',
    label: 'คีย์ลงนามรายงาน',
    hint: 'ใช้ลงนามรายงานที่ส่งออก เพื่อตรวจสอบที่มาของไฟล์',
  },
] as const

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

export function Vault() {
  const qc = useQueryClient()
  const overview = useQuery({
    queryKey: ['vault-keys'],
    queryFn: () => api<VaultOverview>('/v1/vault/keys'),
    staleTime: 15_000,
  })
  const rotate = useMutation({
    mutationFn: (purpose: string) => apiWrite<VaultKey>(`/v1/vault/keys/${purpose}/rotate`, 'POST', {}),
    onSuccess: (key) => {
      void qc.invalidateQueries({ queryKey: ['vault-keys'] })
      const label = PURPOSES.find((p) => p.id === key.purpose)?.label ?? key.purpose
      setNotice(`หมุนเวียน${label}เป็นเวอร์ชัน ${key.version} แล้ว เวอร์ชันก่อนหน้าปลดระวางแต่ยังเก็บไว้ในประวัติ`)
    },
  })

  const [confirm, setConfirm] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const rows = overview.data?.keys ?? []
  const maxAge = overview.data?.max_age_days ?? 90
  const activeCount = rows.filter((k) => k.active).length

  const startRotate = (purpose: string) => {
    setNotice(null)
    rotate.reset()
    setConfirm(purpose)
  }

  return (
    <>
      <PageHead
        title="คลังกุญแจเข้ารหัส"
        sub="คีย์ข้อมูลรายองค์กรสำหรับ MFA ฟิลด์ข้อมูล API key และการลงนามรายงาน"
      />

      <div className="card card-pad">
        <div className="row" style={{ gap: 18, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 340px' }}>
            <div className="card-label">นโยบายการหมุนเวียน</div>
            <p className="dim" style={{ fontSize: 12.5, marginTop: 10, lineHeight: 1.8 }}>
              แต่ละประเภทมีคีย์ใช้งานหนึ่งเวอร์ชัน และควรหมุนเวียนทุก {maxAge} วัน
              เมื่อหมุนเวียน ระบบจะสร้างเวอร์ชันใหม่และปลดระวางเวอร์ชันเดิม
              โดยไม่ลบประวัติ เพื่อให้ข้อมูลที่เข้ารหัสไว้ก่อนยังเปิดอ่านได้ระหว่างการย้ายคีย์
            </p>
          </div>
          <div style={{ minWidth: 150 }}>
            <div className="eyebrow">คีย์ที่ใช้งานอยู่</div>
            <div className="stat-value" style={{ fontSize: 30 }}>
              {activeCount}
              <span className="stat-unit"> จาก {PURPOSES.length}</span>
            </div>
          </div>
        </div>
        <div
          className="dim"
          style={{
            marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--color-hairline)',
            fontSize: 11.5, lineHeight: 1.7,
          }}
        >
          ระบบเก็บเฉพาะข้อความเข้ารหัสของคีย์ ไม่แสดงตัวคีย์ผ่าน API หรือหน้าจอนี้
          และไม่บันทึกตัวคีย์ลง audit trail
        </div>
      </div>

      {notice ? (
        <div
          role="status"
          style={{
            marginTop: 16, padding: '10px 14px', fontSize: 12.5, lineHeight: 1.7,
            border: '1px solid var(--color-emerald-soft)', borderRadius: 6,
            color: 'var(--color-emerald-soft)',
          }}
        >
          {notice}
        </div>
      ) : null}

      {rotate.isError ? (
        <div className="form-error" style={{ marginTop: 16 }}>
          {errorText(rotate.error, 'หมุนเวียนคีย์ไม่สำเร็จ')}
        </div>
      ) : null}

      {overview.isLoading ? <div style={{ marginTop: 18 }}><Skel h={320} /></div> : null}

      {overview.isError ? (
        <div className="form-error" style={{ marginTop: 18 }}>
          {errorText(overview.error, 'โหลดคลังกุญแจไม่สำเร็จ')}
        </div>
      ) : null}

      {!overview.isLoading && !overview.isError
        ? PURPOSES.map((meta) => {
            const keys = rows
              .filter((k) => k.purpose === meta.id)
              .sort((a, b) => b.version - a.version)
            const active = keys.find((k) => k.active)
            const stale = active ? active.rotate_recommended || active.age_days >= maxAge : false

            return (
              <div className="card card-pad" key={meta.id} style={{ marginTop: 16 }}>
                <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  <div style={{ flex: '1 1 300px' }}>
                    <div className="card-label">{meta.label}</div>
                    <div className="dim" style={{ fontSize: 12, marginTop: 6, lineHeight: 1.7 }}>
                      {meta.hint}
                    </div>
                  </div>
                  <span className={`chip${active ? ' gold' : ''}`} style={{ marginLeft: 'auto' }}>
                    {active ? `ใช้งาน · เวอร์ชัน ${active.version}` : 'ยังไม่มีคีย์'}
                  </span>
                </div>

                {stale && active ? (
                  <div
                    role="status"
                    style={{
                      marginTop: 14, padding: '10px 12px', fontSize: 12.5, lineHeight: 1.7,
                      border: '1px solid var(--color-amber)', borderRadius: 6,
                      color: 'var(--color-amber)',
                    }}
                  >
                    คีย์เวอร์ชัน {active.version} ใช้งานมา {active.age_days} วัน
                    เกินกำหนด {maxAge} วันแล้ว ควรหมุนเวียนเพื่อจำกัดระยะเวลาที่ข้อมูลผูกกับคีย์เดิม
                  </div>
                ) : null}

                {keys.length === 0 ? (
                  <div className="dim" style={{ fontSize: 12.5, marginTop: 14 }}>
                    ยังไม่มีคีย์สำหรับประเภทนี้ — ระบบจะสร้างเวอร์ชันแรกเมื่อโหลดหน้าอีกครั้ง
                  </div>
                ) : (
                  <div className="table-wrap" style={{ marginTop: 14 }}>
                    <table className="table data">
                      <thead>
                        <tr>
                          <th>เวอร์ชัน</th>
                          <th>สถานะ</th>
                          <th>สร้างเมื่อ</th>
                          <th>ปลดระวางเมื่อ</th>
                          <th>อายุ</th>
                          <th>จัดการ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {keys.map((k) => (
                          <tr key={k.id} style={{ cursor: 'default' }}>
                            <td className="mono">v{k.version}</td>
                            <td>
                              <span className={`chip${k.active ? ' gold' : ''}`}>
                                {k.active ? 'ใช้งานอยู่' : 'ปลดระวางแล้ว'}
                              </span>
                            </td>
                            <td className="dim">{thaiDate(k.created_at)}</td>
                            <td className="dim">{k.retired_at ? thaiDate(k.retired_at) : '—'}</td>
                            <td className="dim">{k.active ? `${k.age_days} วัน` : '—'}</td>
                            <td style={{ textAlign: 'right' }}>
                              {!k.active ? (
                                <span className="dim">—</span>
                              ) : confirm === meta.id ? (
                                <span className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
                                  <span className="dim" style={{ fontSize: 11.5 }}>
                                    สร้าง v{k.version + 1} และปลดระวาง v{k.version}
                                  </span>
                                  <button
                                    className="btn btn-sm"
                                    disabled={rotate.isPending}
                                    onClick={() => rotate.mutate(meta.id, { onSuccess: () => setConfirm(null) })}
                                    style={{ color: 'var(--color-amber)' }}
                                  >
                                    {rotate.isPending ? 'กำลังหมุนเวียน…' : 'ยืนยัน'}
                                  </button>
                                  <button
                                    className="btn btn-ghost btn-sm"
                                    disabled={rotate.isPending}
                                    onClick={() => setConfirm(null)}
                                  >
                                    ยกเลิก
                                  </button>
                                </span>
                              ) : (
                                <button
                                  className="btn btn-ghost btn-sm"
                                  disabled={rotate.isPending}
                                  onClick={() => startRotate(meta.id)}
                                >
                                  หมุนเวียนคีย์
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )
          })
        : null}
    </>
  )
}
